import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, ImagePlus, Send, Loader2, Headphones } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/ref`;
const DOWNLOAD = "https://centraldesupport.online/suporte";

function useRefChat(code) {
  const key = `ref_chat_${code}`;
  const [st, setSt] = useState(() => { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } });
  const [msgs, setMsgs] = useState([]);
  const [typing, setTyping] = useState(false);
  const [error, setError] = useState("");
  const last = useRef("");

  const start = useCallback(async () => {
    const r = await fetch(`${BASE}/start/${encodeURIComponent(code)}`, { method: "POST" });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setError(d.detail || "Não foi possível abrir o atendimento."); return; }
    const s = { sid: d.session_id, token: d.token, name: d.name };
    localStorage.setItem(key, JSON.stringify(s));
    last.current = ""; setMsgs([]); setSt(s);
  }, [code, key]);

  const req = useCallback(async (method, path, body, isForm) => {
    const h = { "X-Chat-Session": st.sid, "X-Chat-Token": st.token };
    if (body && !isForm) h["Content-Type"] = "application/json";
    const r = await fetch(BASE + path, { method, headers: h, body: isForm ? body : body ? JSON.stringify(body) : undefined });
    if (r.status === 401) { localStorage.removeItem(key); start(); throw new Error("Sessão renovada, tente de novo."); }
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Não foi possível enviar.");
    return d;
  }, [st, key, start]);

  useEffect(() => { if (!st) start(); }, [st, start]);

  useEffect(() => {
    if (!st) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const d = await req("GET", `/poll?after=${encodeURIComponent(last.current)}`);
        if (!alive) return;
        setTyping(d.typing);
        if (d.messages.length) {
          last.current = d.messages[d.messages.length - 1].at;
          setMsgs((m) => { const ids = new Set(m.map((x) => x.id)); return [...m, ...d.messages.filter((x) => !ids.has(x.id))]; });
        }
      } catch { /* retried on next tick */ }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [st, req]);

  const add = (m) => setMsgs((x) => (x.some((y) => y.id === m.id) ? x : [...x, m]));
  const send = async (text) => add(await req("POST", "/messages", { text }));
  const upload = async (file) => { const f = new FormData(); f.append("file", file); add(await req("POST", "/upload", f, true)); };
  return { st, msgs, typing, error, send, upload };
}

const Bubble = ({ m, st }) => {
  const mine = m.from === "visitor";
  const src = m.file && `${BASE}/files/${m.file.id}?sid=${st.sid}&t=${st.token}`;
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} animate-[fadeIn_.25s_ease]`} data-testid={`ref-msg-${m.id}`}>
      <div className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${mine ? "bg-emerald-500 text-[#04130C] rounded-br-md" : "bg-white/[0.06] text-slate-100 rounded-bl-md border border-white/10"}`}>
        {src && (m.file.type || "").startsWith("image/") ? <img src={src} alt={m.file.name} className="rounded-lg max-h-64 mb-1" /> : null}
        {src && !(m.file.type || "").startsWith("image/") ? <a href={src} className="underline">{m.file.name}</a> : null}
        {m.text}
      </div>
    </div>
  );
};

function Composer({ send, upload }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const file = useRef(null);
  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(e.message); } setBusy(false); };
  const submit = (e) => { e.preventDefault(); const t = text.trim(); if (!t) return; run(async () => { await send(t); setText(""); }); };
  return (
    <form onSubmit={submit} className="border-t border-white/10 p-3 bg-[#0E1420]">
      {err && <p className="text-xs text-rose-400 mb-2" data-testid="ref-chat-error">{err}</p>}
      <div className="flex items-center gap-2">
        <input ref={file} type="file" accept="image/*" hidden data-testid="ref-chat-file-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) run(() => upload(f)); }} />
        <button type="button" onClick={() => file.current?.click()} disabled={busy} data-testid="ref-chat-photo-btn"
          className="h-11 w-11 shrink-0 grid place-items-center rounded-full bg-white/[0.06] text-slate-300 hover:bg-white/10 transition-colors" aria-label="Enviar foto">
          <ImagePlus className="h-5 w-5" />
        </button>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Escreva sua mensagem" maxLength={2000} data-testid="ref-chat-input"
          className="flex-1 h-11 rounded-full bg-white/[0.06] border border-white/10 px-4 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-emerald-400/60" />
        <button type="submit" disabled={busy || !text.trim()} data-testid="ref-chat-send-btn"
          className="h-11 w-11 shrink-0 grid place-items-center rounded-full bg-emerald-500 text-[#04130C] disabled:opacity-40 hover:bg-emerald-400 transition-colors" aria-label="Enviar">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </button>
      </div>
    </form>
  );
}

export default function RefChat() {
  const { code } = useParams();
  const { st, msgs, typing, error, send, upload } = useRefChat(code);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length, typing]);
  return (
    <div className="min-h-[100dvh] bg-[#0B0F17] text-slate-100 flex justify-center" data-testid="ref-chat-page">
      <div className="w-full max-w-xl flex flex-col h-[100dvh]">
        <header className="px-5 pt-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-emerald-500/15 text-emerald-400 grid place-items-center"><Headphones className="h-5 w-5" /></div>
            <div>
              <h1 className="text-lg font-bold tracking-tight" data-testid="ref-chat-title">{st?.name ? `Olá, ${st.name}` : "Atendimento"}</h1>
              <p className="text-xs text-slate-400">Seu atendimento já está identificado</p>
            </div>
          </div>
          <a href={DOWNLOAD} target="_blank" rel="noreferrer" data-testid="ref-download-app-btn"
            className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-emerald-500 text-[#04130C] px-4 py-3 font-semibold hover:bg-emerald-400 transition-colors">
            <span className="text-sm">Baixar nosso app de suporte</span><Download className="h-5 w-5" />
          </a>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3" data-testid="ref-chat-messages">
          {error && <p className="text-center text-sm text-rose-400 mt-10" data-testid="ref-chat-invalid">{error}</p>}
          {!error && !st && <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-slate-500" />}
          {st && msgs.map((m) => <Bubble key={m.id} m={m} st={st} />)}
          {typing && <p className="text-xs text-slate-500" data-testid="ref-chat-typing">digitando…</p>}
          <div ref={end} />
        </div>
        {st && !error && <Composer send={send} upload={upload} />}
      </div>
    </div>
  );
}
